import React, { useState, useEffect } from 'react';
import { X, ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useCookies } from '../hooks/useCookies';

const CookieBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const { language } = useLanguage();
  const { hasAcceptedCookies, acceptCookies, rejectCookies } = useCookies();
  const [cookiePreferences, setCookiePreferences] = useState({
    necessary: true, // Toujours true car nécessaire
    analytics: false,
    marketing: false,
    preferences: false
  });
  
  useEffect(() => {
    // Vérifie si l'utilisateur a déjà fait son choix concernant les cookies
    const cookieChoice = hasAcceptedCookies();
    if (cookieChoice === undefined) {
      setIsVisible(true);
    }
  }, [hasAcceptedCookies]);

  const handleAcceptAll = () => {
    setCookiePreferences({
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true
    });
    acceptCookies();
    setIsVisible(false);
    window.location.reload();
  };

  const handleRejectAll = () => {
    setCookiePreferences({
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false
    });
    rejectCookies();
    setIsVisible(false);
  };

  const handleSavePreferences = () => {
    if (cookiePreferences.analytics || cookiePreferences.marketing || cookiePreferences.preferences) {
      acceptCookies();
    } else {
      rejectCookies();
    }
    setIsVisible(false);
    if (cookiePreferences.analytics) {
      window.location.reload();
    }
  };

  const content = {
    fr: {
      title: "🍪 Paramètres des cookies",
      description: "Nous utilisons des cookies pour améliorer votre expérience sur notre site. Vous pouvez personnaliser vos préférences ci-dessous.",
      acceptAll: "Tout accepter",
      rejectAll: "Tout refuser",
      save: "Enregistrer les préférences",
      showDetails: "Afficher les détails",
      hideDetails: "Masquer les détails",
      policy: "Politique de confidentialité",
      categories: {
        necessary: {
          title: "Cookies nécessaires",
          description: "Ces cookies sont essentiels au fonctionnement du site et ne peuvent pas être désactivés."
        },
        analytics: {
          title: "Cookies analytiques",
          description: "Nous permettent de comprendre comment vous utilisez le site pour l'améliorer."
        },
        marketing: {
          title: "Cookies marketing",
          description: "Utilisés pour vous proposer des publicités pertinentes sur d'autres sites."
        },
        preferences: {
          title: "Cookies de préférences",
          description: "Permettent de mémoriser vos préférences pour une meilleure expérience."
        }
      }
    },
    en: {
      title: "🍪 Cookie Settings",
      description: "We use cookies to enhance your experience on our website. You can customize your preferences below.",
      acceptAll: "Accept all",
      rejectAll: "Reject all",
      save: "Save preferences",
      showDetails: "Show details",
      hideDetails: "Hide details",
      policy: "Privacy Policy",
      categories: {
        necessary: {
          title: "Necessary cookies",
          description: "These cookies are essential for the website to function and cannot be disabled."
        },
        analytics: {
          title: "Analytics cookies",
          description: "Help us understand how you use the website to improve it."
        },
        marketing: {
          title: "Marketing cookies",
          description: "Used to serve you relevant ads on other websites."
        },
        preferences: {
          title: "Preference cookies",
          description: "Allow the website to remember your preferences for a better experience."
        }
      }
    }
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 bg-white border-t border-gray-200 shadow-lg z-50 p-4 md:p-6">
      <div className="container mx-auto max-w-4xl">
        <div className="relative">
          <button
            onClick={handleRejectAll}
            className="absolute top-0 right-0 text-gray-400 hover:text-gray-600 transition-colors duration-200"
          >
            <X size={20} />
          </button>

          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {content[language].title}
            </h3>
            <p className="text-gray-600 text-sm md:text-base">
              {content[language].description}
            </p>
          </div>

          <button
            onClick={() => setShowDetails(!showDetails)}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-700 transition-colors duration-200 text-sm font-medium mb-4"
          >
            {showDetails ? content[language].hideDetails : content[language].showDetails}
            <ChevronDown
              size={16}
              className={`transform transition-transform duration-200 ${
                showDetails ? 'rotate-180' : ''
              }`}
            />
          </button>

          {showDetails && (
            <div className="space-y-4 mb-6 bg-gray-50 p-4 rounded-lg">
              {Object.entries(cookiePreferences).map(([key, value]) => (
                <div key={key} className="flex items-start gap-3">
                  <div className="pt-0.5">
                    <input
                      type="checkbox"
                      id={key}
                      checked={value}
                      onChange={(e) =>
                        key !== 'necessary' &&
                        setCookiePreferences((prev) => ({
                          ...prev,
                          [key]: e.target.checked,
                        }))
                      }
                      disabled={key === 'necessary'}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </div>
                  <div className="flex-1">
                    <label
                      htmlFor={key}
                      className="block text-sm font-medium text-gray-900 mb-1"
                    >
                      {content[language].categories[key].title}
                    </label>
                    <p className="text-xs text-gray-500">
                      {content[language].categories[key].description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
            <button
              onClick={handleAcceptAll}
              className="flex-1 px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 text-sm font-medium"
            >
              {content[language].acceptAll}
            </button>
            <button
              onClick={handleRejectAll}
              className="flex-1 px-6 py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors duration-200 text-sm font-medium"
            >
              {content[language].rejectAll}
            </button>
            {showDetails && (
              <button
                onClick={handleSavePreferences}
                className="flex-1 px-6 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200 text-sm font-medium"
              >
                {content[language].save}
              </button>
            )}
            <Link
              to="/privacy-policy"
              className="flex-1 px-6 py-2.5 text-gray-600 hover:text-gray-900 transition-colors duration-200 text-sm font-medium text-center"
            >
              {content[language].policy}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner; 